import {
  type F2fTransactionStatus,
  type OnlineTransactionStatus,
} from "@gen/app-query";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { StatusChip } from "~/components/status_chip";
import { useConfig } from "~/contexts/config";

interface TimelineStatus {
  name: string;
  timestamp: Date;
}

interface TransactionTimelineSectionProps {
  timeline: TimelineStatus[];
  statusData?: {
    f2f?: F2fTransactionStatus[];
    online?: OnlineTransactionStatus[];
  };
}

export function TransactionTimelineSection({
  timeline,
  statusData,
}: TransactionTimelineSectionProps) {
  const { localization } = useConfig();
  const theme = useTheme();

  const f2fStatusMap = new Map(
    (statusData?.f2f ?? []).map((s) => [s.label, s]),
  );
  const onlineStatusMap = new Map(
    (statusData?.online ?? []).map((s) => [s.label, s]),
  );

  const getStatusColor = (milestone: string) =>
    f2fStatusMap.get(milestone)?.color ??
    onlineStatusMap.get(milestone)?.color ??
    theme.palette.primary.main;

  const formatTimestamp = (timestamp: Date) =>
    new Date(timestamp)
      .toLocaleString(localization.locale, {
        timeZone: localization.timeZone,
      })
      .split(", ");

  const normalizeName = (value: string) =>
    value.toLowerCase().trim().replaceAll(" ", "-");

  const getStatusCode = (milestone: string) =>
    f2fStatusMap.get(milestone)?.code ??
    onlineStatusMap.get(milestone)?.code ??
    normalizeName(milestone);

  const dateTimeRow = (date: string, time: string, dataTestId: string) => (
    <Stack flexDirection="row" alignItems="baseline" data-testid={dataTestId}>
      <Typography variant="body2">{date}&nbsp;</Typography>
      <Typography variant="caption">{time}</Typography>
    </Stack>
  );

  const buildTimelineItem = (
    status: TimelineStatus,
    index: number,
    last: boolean,
  ) => {
    const [date, time] = formatTimestamp(status.timestamp);
    const code = getStatusCode(status.name);
    return (
      <TimelineItem key={`${status.name}-${index}`}>
        <TimelineOppositeContent>
          <StatusChip
            label={status.name}
            color={getStatusColor(status.name)}
            dataTestId={`timeline-${code}-status-text`}
          />
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot variant={last ? "outlined" : "filled"} />
          {!last && <TimelineConnector />}
        </TimelineSeparator>
        <TimelineContent>
          {dateTimeRow(date, time, `timeline-${code}-date-text`)}
        </TimelineContent>
      </TimelineItem>
    );
  };

  const renderTimeline = () => {
    if (!timeline || timeline.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No timeline data available
        </Typography>
      );
    }

    return (
      <Timeline position="right" sx={{ pt: 2 }}>
        {timeline.map((status, idx) =>
          buildTimelineItem(status, idx, idx === timeline.length - 1),
        )}
      </Timeline>
    );
  };

  return (
    <Stack
      spacing={2}
      sx={{
        mb: 5,
        pt: 3,
        pr: 3,
        pl: 3,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5SolidDivider"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PublicSvgIcon name="calendar_month" /> Timeline
      </Typography>

      <Box>{renderTimeline()}</Box>
    </Stack>
  );
}
